import type { BookingSummary } from "@/types";

export type FichaMetric = {
  value: string;
  label: string;
  hint: string;
};

export function money(cents: number, currency: string) {
  const amount = cents / 100;
  if (currency === "MXN") return `$${amount.toLocaleString("es-MX")} MXN`;
  return `$${amount.toLocaleString("es-MX")} ${currency}`;
}

export function formatDate(iso: string | null, timezone: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: timezone,
  });
}

export function formatWhen(iso: string, timezone: string) {
  return new Date(iso).toLocaleString("es-MX", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone,
  });
}

function revenueMetric(analytics: BookingSummary): FichaMetric | null {
  if (analytics.revenue_cents === undefined) return null;
  return {
    value: money(analytics.revenue_cents, analytics.currency),
    label: "Ingresos estimados",
    hint: "precio de lista × citas completadas",
  };
}

function gapMetric(
  analytics: BookingSummary,
  label: "Recurrencia" | "Cadencia",
): FichaMetric {
  const empty =
    label === "Recurrencia"
      ? "hace falta una segunda visita"
      : "hace falta una segunda cita completada";
  const filled =
    label === "Recurrencia"
      ? "mediana entre visitas completadas"
      : "mediana entre citas completadas";
  return {
    value:
      analytics.recurrence_days != null
        ? `${analytics.recurrence_days} días`
        : "—",
    label,
    hint: analytics.recurrence_days == null ? empty : filled,
  };
}

function completedMetric(analytics: BookingSummary): FichaMetric {
  return {
    value: String(analytics.completed_count),
    label: "Citas completadas",
    hint: `${analytics.cancelled_count} canceladas · ${analytics.no_show_count} no show`,
  };
}

function lastVisitMetric(
  analytics: BookingSummary,
  timezone: string,
  label = "Última visita",
): FichaMetric {
  return {
    value: formatDate(analytics.last_completed_at, timezone),
    label,
    hint: analytics.first_completed_at
      ? `desde ${formatDate(analytics.first_completed_at, timezone)}`
      : "sin visitas completadas",
  };
}

function occupancyMetric(analytics: BookingSummary): FichaMetric | null {
  if (analytics.occupancy_pct == null) return null;
  return {
    value: `${analytics.occupancy_pct}%`,
    label: "Ocupación",
    hint: `${analytics.booked_hours ?? 0}h / ${analytics.offered_hours ?? 0}h ofertadas`,
  };
}

function uniqueClientsMetric(analytics: BookingSummary): FichaMetric {
  return {
    value: String(analytics.unique_clients),
    label: "Clientes",
    hint: "distintos en este periodo",
  };
}

function vsOtherMetric(analytics: BookingSummary): FichaMetric | null {
  if (!analytics.vs_other) return null;
  const sign = analytics.vs_other.delta_pct > 0 ? "+" : "";
  return {
    value: `${sign}${analytics.vs_other.delta_pct}%`,
    label: `Vs. ${analytics.vs_other.name}`,
    hint: "misma ventana",
  };
}

function compact(metrics: (FichaMetric | null)[]): FichaMetric[] {
  return metrics.filter((metric): metric is FichaMetric => metric != null);
}

export function clientMetrics(
  analytics: BookingSummary,
  timezone: string,
): FichaMetric[] {
  return compact([
    revenueMetric(analytics),
    gapMetric(analytics, "Recurrencia"),
    completedMetric(analytics),
    lastVisitMetric(analytics, timezone),
  ]);
}

export function staffMetrics(
  analytics: BookingSummary,
  _timezone: string,
): FichaMetric[] {
  return compact([
    revenueMetric(analytics),
    gapMetric(analytics, "Cadencia"),
    completedMetric(analytics),
    uniqueClientsMetric(analytics),
    occupancyMetric(analytics),
  ]);
}

export function serviceMetrics(
  analytics: BookingSummary,
  timezone: string,
): FichaMetric[] {
  return compact([
    revenueMetric(analytics),
    gapMetric(analytics, "Cadencia"),
    completedMetric(analytics),
    uniqueClientsMetric(analytics),
    lastVisitMetric(analytics, timezone, "Última venta"),
  ]);
}

export function spaceMetrics(
  analytics: BookingSummary,
  _timezone: string,
): FichaMetric[] {
  return compact([
    revenueMetric(analytics),
    gapMetric(analytics, "Cadencia"),
    completedMetric(analytics),
    uniqueClientsMetric(analytics),
    occupancyMetric(analytics),
  ]);
}

export function branchMetrics(
  analytics: BookingSummary,
  _timezone: string,
): FichaMetric[] {
  return compact([
    revenueMetric(analytics),
    gapMetric(analytics, "Cadencia"),
    completedMetric(analytics),
    uniqueClientsMetric(analytics),
    occupancyMetric(analytics),
    vsOtherMetric(analytics),
  ]);
}
